if (!customElements.get('parallax-products-banner')) {
  customElements.define(
    'parallax-products-banner',
    class RovyVonHeroCarousel extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('[data-carousel-track]');
        this.slides = Array.from(this.querySelectorAll('[data-banner-item]'));
        this.prevButton = this.querySelector('[data-carousel-prev]');
        this.nextButton = this.querySelector('[data-carousel-next]');
        this.progressBar = this.querySelector('[data-carousel-progress-bar]');

        this.activeIndex = 0;
        this.duration = 6000;
        this.timer = null;
        this.raf = null;
        this.startedAt = 0;
        this.remaining = this.duration;
        this.paused = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.onScroll = this.onScroll.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.pauseAutoplay = this.pauseAutoplay.bind(this);
        this.resumeAutoplay = this.resumeAutoplay.bind(this);
      }

      connectedCallback() {
        if (!this.track || this.slides.length < 1) return;

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('keydown', this.onKeydown);
        this.prevButton?.addEventListener('click', this.onPrev);
        this.nextButton?.addEventListener('click', this.onNext);

        this.addEventListener('mouseenter', this.pauseAutoplay);
        this.addEventListener('mouseleave', this.resumeAutoplay);
        this.addEventListener('focusin', this.pauseAutoplay);
        this.addEventListener('focusout', this.resumeAutoplay);
        this.track.addEventListener('pointerdown', this.pauseAutoplay, { passive: true });
        this.track.addEventListener('pointerup', this.resumeAutoplay, { passive: true });
        this.track.addEventListener('pointercancel', this.resumeAutoplay, { passive: true });

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
        }

        this.updateUI(0);

        if (this.slides.length > 2 && !this.reducedMotion) {
          this.restartAutoplay();
        } else {
          this.setProgressVisible(false);
        }
      }

      disconnectedCallback() {
        if (this.raf) cancelAnimationFrame(this.raf);
        if (this.timer) clearTimeout(this.timer);

        this.track?.removeEventListener('scroll', this.onScroll);
        this.track?.removeEventListener('keydown', this.onKeydown);
        this.prevButton?.removeEventListener('click', this.onPrev);
        this.nextButton?.removeEventListener('click', this.onNext);

        this.removeEventListener('mouseenter', this.pauseAutoplay);
        this.removeEventListener('mouseleave', this.resumeAutoplay);
        this.removeEventListener('focusin', this.pauseAutoplay);
        this.removeEventListener('focusout', this.resumeAutoplay);
        this.track?.removeEventListener('pointerdown', this.pauseAutoplay);
        this.track?.removeEventListener('pointerup', this.resumeAutoplay);
        this.track?.removeEventListener('pointercancel', this.resumeAutoplay);

        if (Shopify.designMode) {
          document.removeEventListener('shopify:block:select', this.onBlockSelect);
        }
      }

      onScroll() {
        if (this.raf) cancelAnimationFrame(this.raf);

        this.raf = requestAnimationFrame(() => {
          const width = this.track.clientWidth || 1;
          const index = Math.max(
            0,
            Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / width))
          );

          if (index !== this.activeIndex) {
            this.updateUI(index);
            if (this.slides.length > 2 && !this.reducedMotion) {
              this.restartAutoplay();
            }
          }
        });
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.activeIndex - 1, true);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.activeIndex + 1, true);
        }
      }

      onPrev() {
        this.goTo(this.activeIndex - 1, true);
      }

      onNext() {
        this.goTo(this.activeIndex + 1, true);
      }

      onBlockSelect(event) {
        const block = event.target?.closest?.('[data-banner-item]');
        if (!block || !this.contains(block)) return;

        const index = this.slides.indexOf(block);
        if (index >= 0) this.goTo(index, false);
      }

      goTo(index, isManual = false) {
        if (!this.track || this.slides.length < 1) return;

        const normalized = (index + this.slides.length) % this.slides.length;

        this.track.scrollTo({
          left: normalized * this.track.clientWidth,
          behavior: this.reducedMotion ? 'auto' : 'smooth'
        });

        this.updateUI(normalized);

        if ((isManual || this.slides.length > 2) && !this.reducedMotion) {
          this.restartAutoplay();
        }
      }

      updateUI(index) {
        this.activeIndex = index;
      }

      restartAutoplay() {
        if (this.slides.length <= 2 || this.reducedMotion) return;

        if (this.timer) clearTimeout(this.timer);

        this.paused = false;
        this.remaining = this.duration;
        this.startedAt = performance.now();
        this.restartProgress(this.duration);

        this.timer = setTimeout(() => {
          this.timer = null;
          this.goTo(this.activeIndex + 1, false);
        }, this.duration);
      }

      pauseAutoplay() {
        if (this.slides.length <= 2 || this.reducedMotion || this.paused || !this.timer) return;

        const elapsed = performance.now() - this.startedAt;
        this.remaining = Math.max(0, this.remaining - elapsed);
        clearTimeout(this.timer);
        this.timer = null;
        this.paused = true;

        if (this.progressBar) {
          this.progressBar.classList.add('is-paused');
        }
      }

      resumeAutoplay() {
        if (this.slides.length <= 2 || this.reducedMotion || !this.paused) return;

        this.paused = false;
        this.startedAt = performance.now();

        if (this.progressBar) {
          this.progressBar.classList.remove('is-paused');
        }

        this.timer = setTimeout(() => {
          this.timer = null;
          this.goTo(this.activeIndex + 1, false);
        }, this.remaining);
      }

      restartProgress(duration) {
        if (!this.progressBar) return;

        this.setProgressVisible(true);
        this.progressBar.classList.remove('is-running', 'is-paused');
        this.progressBar.style.animationDuration = duration + 'ms';
        void this.progressBar.offsetWidth;
        this.progressBar.classList.add('is-running');
      }

      setProgressVisible(visible) {
        const progress = this.querySelector('[data-carousel-progress]');
        if (progress) progress.hidden = !visible;
      }
    }
  );
}
